import { Button, Card, FontAwesome, Badge } from 'Elements';
import { useTranslation } from 'react-i18next';
import { JobVisibilitySetting } from 'src/types/Job';
import styles from './locations.module.scss';
import { Col, Row, Tooltip } from 'reactstrap';
import { useState } from 'react';
import { LocationListDrawer } from './LocationListDrawer';
import { Colors } from 'src/types/Color';
import { useFormContext } from 'react-hook-form';
import { clx, formatLocalAddress, isAddressMatch } from 'src/helpers/Utils';
import { useInfinityBranchList } from 'src/hooks/useBranch';
import { Link } from 'react-router-dom';

function Locations() {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const toggleTooltip = () => setTooltipOpen(!tooltipOpen);
	const [keyword, setKeyword] = useState('');

	const {
		data: branchData,
		isLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage
	} = useInfinityBranchList(
		{
			pageSize: 30,
			keyword: keyword
		},
		{
			enabled: isOpen
		}
	);

	// Flatten paginated data for easy access
	const allBranches =
		branchData?.pages?.flatMap((page) => page?.items || []).concat() || [];

	const valueConfig = {
		shouldDirty: true,
		shouldValidate: true
	};

	const {
		setValue,
		watch,
		formState: { errors }
	} = useFormContext<JobVisibilitySetting>();

	const onAddLocation = (arr) => {
		const locations = watch('jobLocations') || [];
		const newList: any = [...locations];
		arr.forEach((element) => {
			newList.push({
				postalCode:
					element?.address?.postalCode ??
					(element?.postalCode as string),
				address: element?.address?.street
					? formatLocalAddress(
							element?.address?.street,
							element?.address?.houseNumber,
							element?.address?.postalCode,
							element?.address?.city,
							element?.address?.state
					  )
					: element?.address,
				primary: element?.primary ?? false,
				regionCode: element?.regionCode as number,
				title: element?.address?.city
					? element?.address?.city
					: (element?.name as string),
				name: element?.name as string,
				locationPoint: {
					longitude: element?.address?.location
						? element?.address?.location?.longitude
						: (element?.locationPoint?.longitude as number),
					latitude: element?.address?.location
						? element?.address?.location?.latitude
						: (element?.locationPoint?.latitude as number)
				}
			});
		});

		setValue('jobLocations', newList, valueConfig);
		setIsOpen(false);
	};

	const onRemoveLocation = (item) => {
		const newList: any = [];
		watch('jobLocations')?.forEach((element) => {
			if (!isAddressMatch(element.address, item.address)) {
				newList.push(element);
			}
		});
		setValue('jobLocations', newList, valueConfig);
	};

	return (
		<Card className="w-100">
			<div className="d-flex align-items-center gap-2">
				<h1 className="fs-5 fw-500">{t('label.locations')}</h1>
				<Button color={Colors.empty} className="p-0" id="tooltip">
					<FontAwesome icon="circle-info" />
					<Tooltip
						toggle={toggleTooltip}
						isOpen={tooltipOpen}
						placement="bottom"
						target="tooltip">
						{t('text.description-locations')}
					</Tooltip>
				</Button>
			</div>
			<Row>
				{watch('jobLocations')?.map((item, index) => {
					return (
						<Col
							sm={12}
							xl={4}
							xxl={4}
							key={index}
							className="h-100">
							<div className={clx('mb-3', styles.cardLocation)}>
								<div className="d-flex justify-content-between align-items-center mb-3">
									<div className="fs-base fw-500 d-flex gap-2">
										<span>{item.title} </span>
										{item.primary && (
											<div>
												<Badge color={Colors.primary}>
													{t('text.primary')}
												</Badge>
											</div>
										)}
									</div>
									<Button
										color={Colors['white-red']}
										onClick={() => {
											onRemoveLocation(item);
										}}
										className="py-2 px-2">
										<FontAwesome
											icon="trash-xmark"
											size="lg"
										/>
									</Button>
								</div>
								<div className="d-flex align-items-center gap-2">
									<FontAwesome
										icon="location-dot"
										size="sm"
										color="text-primary"
									/>
									<span>{item.address}</span>
								</div>
							</div>
						</Col>
					);
				})}
				<Col sm={12} xl={4} xxl={4} className="h-100">
					<div
						className={clx('mb-3', styles.addCardLocation)}
						onClick={() => setIsOpen((prevState) => !prevState)}>
						<div className="d-flex align-items-center gap-3">
							<FontAwesome
								icon="plus"
								className={'text-primary'}
								size="2x"
								swapOpacity
							/>
							<span className={`fs-5 fw-500  ${'text-primary'}`}>
								{t('button.add-available-location')}
							</span>
						</div>
					</div>
				</Col>
			</Row>
			<Row>
				{errors.jobLocations && (
					<div
						className="d-flex align-items-center gap-3 bg-yellow_o-2 text-warning p-3 fs-sm fw-500 mt-2"
						style={{ borderRadius: '8px' }}>
						<FontAwesome icon="circle-info" size="2x" />
						<div className="fs-sm fw-500">
							{t('label.location-required')}
							<Link
								to={`/branches/create?from=${window.location.pathname}`}
								className="text-decoration-none text-primary ms-2">
								{t('menu.branches')}
							</Link>
						</div>
					</div>
				)}
			</Row>
			<LocationListDrawer
				isOpen={isOpen}
				data={allBranches}
				hasNextPage={hasNextPage || false}
				isFetchingNextPage={isFetchingNextPage}
				fetchNextPage={fetchNextPage}
				keyword={keyword}
				setKeyword={setKeyword}
				isLoading={isLoading as boolean}
				locations={watch('jobLocations')}
				toggleDrawer={() => {
					setIsOpen((prevState) => !prevState);
				}}
				onAdd={onAddLocation}
			/>
		</Card>
	);
}

export default Locations;
